// Shared UI styles for tournament components
// Import: import { colors, shadows, text, table, btn, form, modal, card, page, empty, spinner } from '../sharedStyles';

export const colors = {
  brand:      'rgba(122,101,149,1)',
  brandLt:    '#e8f0fe',
  success:    '#0e9f6e',
  successLt:  '#d5f5ec',
  danger:     '#e02424',
  dangerLt:   '#fde8e8',
  amber:      '#ff8800',
  amberLt:    '#fff3e0',
  bg:         '#f4f6fb',
  surface:    '#ffffff',
  border:     '#e3e8f0',
  text:       '#111827',
  muted:      '#6b7280',
  altRow:     '#fafbfd',
  tableHead:  '#f8fafc',
};

export const shadows = {
  card: '0 1px 4px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.06)',
  modal: '0 8px 40px rgba(0,0,0,0.18)',
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const page = {
  wrap: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    boxSizing: 'border-box',
  },
  padded: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    padding: '24px 20px',
    boxSizing: 'border-box',
  },
};

// ─── Breadcrumb ───────────────────────────────────────────────────────────────

export const breadcrumb = {
  wrap: {
    padding: '14px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    color: colors.muted,
    flexWrap: 'wrap',
  },
  link: {
    color: colors.brand,
    textDecoration: 'none',
    fontWeight: 600,
  },
  separator: {
    color: '#d1d5db',
  },
  current: {
    color: colors.text,
    fontWeight: 600,
  },
};

// ─── Card ─────────────────────────────────────────────────────────────────────

export const card = {
  wrap: {
    background: colors.surface,
    borderRadius: 12,
    boxShadow: shadows.card,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 24px',
    borderBottom: `1px solid ${colors.border}`,
    flexWrap: 'wrap',
    gap: 10,
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    color: colors.text,
    margin: 0,
  },
  titleCount: {
    marginLeft: 10,
    fontSize: 13,
    fontWeight: 500,
    color: colors.muted,
  },
  body: {
    padding: '20px 24px',
  },
  bodyBordered: {
    padding: '20px 24px',
    borderBottom: `1px solid ${colors.border}`,
  },
};

// ─── Buttons ──────────────────────────────────────────────────────────────────

const btnBase = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontWeight: 600,
  transition: 'opacity 0.15s, transform 0.1s',
};

export const btn = {
  primary: {
    ...btnBase,
    padding: '8px 16px',
    fontSize: 13,
    background: colors.brand,
    color: '#ffffff',
  },
  outline: {
    ...btnBase,
    padding: '8px 16px',
    fontSize: 13,
    color: colors.brand,
    border: `1px solid ${colors.brand}`
  },
  cancel: {
    ...btnBase,
    padding: '8px 16px',
    fontSize: 13,
    background: '#f3f4f6',
    color: colors.text,
  },
  danger: {
    ...btnBase,
    padding: '8px 16px',
    fontSize: 13,
    background: colors.danger,
    color: '#ffffff',
  },
  dangerLt: {
    ...btnBase,
    padding: '6px 12px',
    fontSize: 12,
    background: colors.dangerLt,
    color: colors.danger,
  },
  outlineSm: {
    ...btnBase,
    padding: '6px 12px',
    fontSize: 12,
    background: colors.brandLt,
    color: colors.brand,
  },
  primarySm: {
    ...btnBase,
    padding: '6px 14px',
    fontSize: 12,
    background: colors.brand,
    color: '#ffffff',
  },
};

export const btnGroup = {
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
};

// ─── Table ────────────────────────────────────────────────────────────────────

export const table = {
  wrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  th: {
    padding: '10px 16px',
    background: colors.tableHead,
    color: colors.muted,
    fontWeight: 600,
    textAlign: 'left',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${colors.border}`,
    fontSize: 11,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  td: {
    padding: '12px 16px',
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    whiteSpace: 'nowrap',
  },
  tdMuted: {
    padding: '12px 16px',
    color: colors.muted,
    borderBottom: `1px solid ${colors.border}`,
    textAlign: 'center',
    fontSize: 12,
    fontWeight: 600,
  },
  tdBold: {
    padding: '12px 16px',
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    whiteSpace: 'nowrap',
    fontWeight: 600,
  },
  tdAmount: {
    padding: '12px 16px',
    borderBottom: `1px solid ${colors.border}`,
    fontWeight: 700,
    color: colors.success,
    whiteSpace: 'nowrap',
  },
  trAlt: {
    background: colors.altRow,
  },
  trTotal: {
    background: colors.brandLt,
    borderTop: `2px solid ${colors.brand}`,
  },
  tdTotalLabel: {
    padding: '12px 16px',
    color: colors.brand,
    fontWeight: 700,
    fontSize: 13,
    borderBottom: 'none',
    whiteSpace: 'nowrap',
  },
  tdTotalAmount: {
    padding: '12px 16px',
    background: colors.brandLt,
    color: colors.brand,
    fontWeight: 700,
    fontSize: 14,
    borderBottom: 'none',
    whiteSpace: 'nowrap',
  },
  tdEmpty: {
    padding: '12px 16px',
    borderBottom: 'none',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 10px',
    background: colors.brandLt,
    color: colors.brand,
    borderRadius: 6,
    fontWeight: 700,
    fontSize: 13,
  },
};

// ─── Form ─────────────────────────────────────────────────────────────────────

export const form = {
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 14,
    flexWrap: 'wrap',
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: colors.muted,
    width: 140,
    flexShrink: 0,
  },
  labelWide: {
    fontSize: 13,
    fontWeight: 600,
    color: colors.muted,
    width: 200,
    flexShrink: 0,
  },
  input: {
    flex: 1,
    minWidth: 160,
    padding: '9px 12px',
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    fontSize: 13,
    fontFamily: 'inherit',
    outline: 'none',
    color: colors.text,
    boxSizing: 'border-box',
  },
  select: {
    flex: 1,
    minWidth: 160,
    padding: '9px 12px',
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    fontSize: 13,
    fontFamily: 'inherit',
    outline: 'none',
    color: colors.text,
    boxSizing: 'border-box',
    background: colors.surface,
  },
  textarea: {
    flex: 1,
    minWidth: 160,
    padding: '9px 12px',
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    fontSize: 13,
    fontFamily: 'inherit',
    outline: 'none',
    color: colors.text,
    boxSizing: 'border-box',
    resize: 'vertical',
  },
  searchWrap: {
    display: 'flex',
    flex: 1,
    minWidth: 160,
  },
  searchInput: {
    flex: 1,
    padding: '9px 12px',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px 0 0 8px',
    fontSize: 13,
    fontFamily: 'inherit',
    outline: 'none',
    color: colors.text,
    boxSizing: 'border-box',
  },
  searchBtn: {
    padding: '9px 14px',
    background: colors.brand,
    color: '#ffffff',
    border: 'none',
    borderRadius: '0 8px 8px 0',
    cursor: 'pointer',
    fontSize: 14,
  },
};

// ─── Modal ────────────────────────────────────────────────────────────────────

export const modal = {
  // Pass this as the `style` prop to react-modal's <Modal> component
  reactModal: {
    overlay: { backgroundColor: 'rgba(17,24,39,.45)', zIndex: 1000 },
    content: {
      top: '50%', left: '50%', right: 'auto', bottom: 'auto',
      marginRight: '-50%', transform: 'translate(-50%,-50%)',
      padding: 0, border: 'none', borderRadius: 16,
      boxShadow: shadows.modal,
      width: '90%', maxWidth: 540, maxHeight: '90vh', overflow: 'auto',
    },
  },
  content: {
    padding: '28px 28px 24px',
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: colors.text,
    margin: '0 0 20px',
  },
  footer: {
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end',
    marginTop: 24,
    paddingTop: 16,
    borderTop: `1px solid ${colors.border}`,
  },
  // For material-ui Dialog content
  dialogContent: {
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    padding: '28px 28px 24px',
    minWidth: 480,
  },
  dialogTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: colors.text,
    marginBottom: 20,
    paddingBottom: 12,
    borderBottom: `1px solid ${colors.border}`,
  },
  dialogFooter: {
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end',
    marginTop: 24,
    paddingTop: 16,
    borderTop: `1px solid ${colors.border}`,
  },
  // For confirm/delete dialogs
  confirmTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: colors.text,
    marginBottom: 8,
  },
  confirmText: {
    fontSize: 13,
    color: colors.muted,
  },
  confirmFooter: {
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end',
    marginTop: 24,
  },
};

// ─── Info detail rows (label + value pairs) ───────────────────────────────────

export const detail = {
  row: {
    display: 'flex',
    gap: 8,
    marginBottom: 12,
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.muted,
    width: 160,
    flexShrink: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    paddingTop: 1,
  },
  value: {
    fontSize: 13,
    color: colors.text,
    fontWeight: 500,
  },
};

// ─── Summary cards ────────────────────────────────────────────────────────────

export const summary = {
  row: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 24,
  },
  card: {
    flex: '1 1 160px',
    background: colors.surface,
    borderRadius: 12,
    boxShadow: shadows.card,
    padding: '18px 22px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  icon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
    fontSize: 18,
  },
  iconBlue:   { background: colors.brandLt,   color: colors.brand   },
  iconGreen:  { background: colors.successLt,  color: colors.success },
  iconAmber:  { background: colors.amberLt,    color: colors.amber   },
  label: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: colors.muted,
  },
  value: {
    fontSize: 28,
    fontWeight: 700,
    color: colors.text,
    lineHeight: 1.2,
  },
};

// ─── Empty state ──────────────────────────────────────────────────────────────

export const empty = {
  wrap: {
    textAlign: 'center',
    padding: '64px 24px',
    color: colors.muted,
  },
  icon: {
    fontSize: 40,
    marginBottom: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: colors.text,
    marginBottom: 6,
  },
  text: {
    fontSize: 14,
  },
};

// ─── Spinner ──────────────────────────────────────────────────────────────────

export const spinner = {
  wrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '60vh',
  },
  color: colors.brand,
};

// ─── Tab bar ──────────────────────────────────────────────────────────────────

export const tabs = {
  wrap: {
    display: 'flex',
    background: colors.surface,
    borderRadius: 12,
    boxShadow: shadows.card,
    overflow: 'hidden',
    marginBottom: 20,
  },
  tab: (active) => ({
    flex: 1,
    padding: '14px 10px',
    textAlign: 'center',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    borderBottom: active ? `3px solid ${colors.brand}` : '3px solid transparent',
    color: active ? colors.brand : colors.muted,
    background: active ? colors.brandLt : 'transparent',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  }),
};

// ─── Search results ───────────────────────────────────────────────────────────

export const search = {
  results: {
    marginBottom: 14,
    paddingLeft: 152,
  },
  item: {
    padding: '10px 14px',
    borderRadius: 8,
    cursor: 'pointer',
    marginBottom: 6,
    fontSize: 13,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    border: `1px solid ${colors.border}`,
  },
  itemMember: {
    background: colors.successLt,
    borderColor: '#6ee7b7',
  },
  itemChild: {
    background: colors.dangerLt,
    borderColor: '#fca5a5',
  },
  name: {
    fontWeight: 600,
    color: colors.text,
  },
  meta: {
    color: colors.muted,
  },
  notFound: {
    background: colors.dangerLt,
    borderRadius: 8,
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  notFoundText: {
    color: colors.danger,
    fontSize: 13,
    fontWeight: 600,
  },
};

// ─── Chip wrapper ─────────────────────────────────────────────────────────────

export const chipWrap = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  flex: 1,
};


// WEBPACK FOOTER //
// ./src/app/style/common/partnerStyle.js